import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare,
  Users,
  Award,
  Search,
  Plus,
  ChevronDown,
  Filter,
  Clock,
  ThumbsUp,
  MessageCircle,
  Edit,
  Trash,
  Send,
  Eye,
  Lock,
  Shield,
  AlertTriangle
} from 'lucide-react';

const CyberAegisForum = ({ apiService, userProfile }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('discussions');
  const [activeCategory, setActiveCategory] = useState('all');
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredTopics, setFilteredTopics] = useState([]);
  const [forumStats, setForumStats] = useState({
    totalTopics: 0,
    totalPosts: 0,
    activeUsers: 0,
    postsToday: 0
  });
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [posts, setPosts] = useState([]);
  const [newPostContent, setNewPostContent] = useState('');
  const [replyToPost, setReplyToPost] = useState(null);
  const [showNewTopicForm, setShowNewTopicForm] = useState(false);
  const [newTopic, setNewTopic] = useState({
    title: '',
    content: '',
    // Default to first arena instead of legacy "general"
    category: 'phishing_social_engineering'
  });
  const replyTextareaRef = useRef(null);
  const [participants, setParticipants] = useState([]);
  const [mentionQuery, setMentionQuery] = useState('');
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionPosition, setMentionPosition] = useState({ start: 0, end: 0 });
  const mentionDropdownRef = useRef(null);

  // Forum categories mapped to learning arenas (arenas = discussion spaces)
  const categories = [
    { id: 'all', name: 'All Arenas', icon: <MessageSquare size={16} /> },
    { id: 'phishing_social_engineering', name: 'Phishing & Social Engineering Arena', icon: <Users size={16} /> },
    { id: 'password_security', name: 'Password Security Arena', icon: <Lock size={16} /> },
    { id: 'data_protection', name: 'Data Protection Arena', icon: <Shield size={16} /> },
    { id: 'network_security', name: 'Network Security Arena', icon: <MessageSquare size={16} /> },
    { id: 'incident_response', name: 'Incident Response Arena', icon: <AlertTriangle size={16} /> },
    { id: 'best_practices', name: 'Best Practices & Tips', icon: <Award size={16} /> }
  ];

  useEffect(() => {
    const fetchTopics = async () => {
      try {
        setLoading(true);
        const response = await apiService.getForumTopics();
        setTopics(response.data || []);
        setFilteredTopics(response.data || []);
      } catch (error) {
        console.error('Error fetching forum topics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTopics();
  }, [apiService]);

  // Fetch forum stats
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await apiService.getForumStats();
        if (response?.data) {
          setForumStats(response.data);
        }
      } catch (error) {
        console.error('Error fetching forum stats:', error);
      }
    };

    fetchStats();
  }, [apiService]);

  // Filter topics based on active category and search query
  useEffect(() => {
    let filtered = [...topics];

    // Apply category filter
    if (activeCategory !== 'all') {
      filtered = filtered.filter(topic => topic.category === activeCategory);
    }

    // Apply search filter
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(topic =>
        topic.title.toLowerCase().includes(query) ||
        topic.preview.toLowerCase().includes(query)
      );
    }

    setFilteredTopics(filtered);
  }, [topics, activeCategory, searchQuery]);

  // Fetch posts for a selected topic
  const handleTopicSelect = async (topicId) => {
    try {
      setLoading(true);
      const response = await apiService.getForumTopicPosts(topicId);
      setPosts(response.data.posts || []);
      setSelectedTopic(response.data.topic);
      // Reset reply state when switching topics
      setReplyToPost(null);
      setNewPostContent('');

      // Fetch all users for @mentions (not just participants)
      const usersResponse = await apiService.getAllUsers();
      setParticipants(usersResponse.data || []);
    } catch (error) {
      console.error('Error fetching topic posts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle text input for mentions
  const handlePostContentChange = async (e) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    setNewPostContent(value);

    // Check if @ is typed
    const textBeforeCursor = value.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      // Check if there's no space after @ (meaning we're still typing a mention)
      if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
        const query = textAfterAt.toLowerCase();
        setMentionQuery(query);
        setShowMentionDropdown(true);
        setMentionPosition({ start: lastAtIndex, end: cursorPos });

        // Fetch users with search query if participants list is empty or query changed
        if (participants.length === 0 || query.length > 0) {
          try {
            const usersResponse = await apiService.getAllUsers(query);
            setParticipants(usersResponse.data || []);
          } catch (error) {
            console.error('Error fetching users for mentions:', error);
          }
        }
        return;
      }
    }

    setShowMentionDropdown(false);
  };

  // Insert mention into text
  const insertMention = (user) => {
    const textarea = replyTextareaRef.current;
    if (!textarea) return;

    const beforeMention = newPostContent.substring(0, mentionPosition.start);
    const afterMention = newPostContent.substring(mentionPosition.end);
    const mentionText = `@${user.name} `;
    const newContent = beforeMention + mentionText + afterMention;

    setNewPostContent(newContent);
    setShowMentionDropdown(false);
    setMentionQuery('');

    // Set cursor position after mention
    setTimeout(() => {
      const newCursorPos = mentionPosition.start + mentionText.length;
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  // Filter participants based on mention query
  const filteredParticipants = participants.filter(p => {
    if (!mentionQuery) return true;
    const nameLower = p.name.toLowerCase();
    const emailLower = (p.email || '').toLowerCase();
    return nameLower.includes(mentionQuery) || emailLower.includes(mentionQuery);
  });

  // Format content with highlighted mentions
  const formatContentWithMentions = (content) => {
    if (!content) return '';
    const parts = [];
    const mentionRegex = /@(\w+(?:\s+\w+)?)/g;
    let lastIndex = 0;
    let match;

    while ((match = mentionRegex.exec(content)) !== null) {
      // Add text before mention
      if (match.index > lastIndex) {
        parts.push(content.substring(lastIndex, match.index));
      }

      // Add highlighted mention
      const mentionText = match[0];
      const userName = match[1];
      parts.push(
        <span key={match.index} className="text-blue-400 font-semibold">
          {mentionText}
        </span>
      );

      lastIndex = mentionRegex.lastIndex;
    }

    // Add remaining text
    if (lastIndex < content.length) {
      parts.push(content.substring(lastIndex));
    }

    return parts.length > 0 ? parts : content;
  };

  // Create a new post in the current topic
  const handleCreatePost = async () => {
    if (!newPostContent.trim() || !selectedTopic) return;

    try {
      await apiService.createForumPost(selectedTopic.id, {
        content: newPostContent,
        parentId: replyToPost ? replyToPost.id : null
      });

      // Refresh posts
      const response = await apiService.getForumTopicPosts(selectedTopic.id);
      setPosts(response.data.posts || []);

      // Clear input and reset reply state
      setNewPostContent('');
      setReplyToPost(null);
    } catch (error) {
      console.error('Error creating post:', error);
    }
  };

  // Create a new topic
  const handleCreateTopic = async () => {
    if (!newTopic.title.trim() || !newTopic.content.trim()) return;

    try {
      await apiService.createForumTopic(newTopic);

      // Refresh topics
      const response = await apiService.getForumTopics();
      setTopics(response.data || []);

      // Reset form
      setNewTopic({
        title: '',
        content: '',
        category: 'general'
      });
      setShowNewTopicForm(false);
    } catch (error) {
      console.error('Error creating topic:', error);
    }
  };

  // Like a post
  const handleLikePost = async (postId) => {
    try {
      await apiService.likeForumPost(postId);

      // Update posts with updated like count
      setPosts(posts.map(post => {
        if (post.id === postId) {
          return {
            ...post,
            likes: post.likes + 1,
            liked: true
          };
        }
        return post;
      }));
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  // Like a topic
  const handleLikeTopic = async (topicId, e) => {
    e.stopPropagation(); // Prevent topic selection when clicking like
    try {
      await apiService.likeForumTopic(topicId);

      // Update topics with updated like count
      setTopics(topics.map(topic => {
        if (topic.id === topicId) {
          return {
            ...topic,
            likes: (topic.likes || 0) + 1,
            liked: true
          };
        }
        return topic;
      }));

      // Also update filtered topics
      setFilteredTopics(filteredTopics.map(topic => {
        if (topic.id === topicId) {
          return {
            ...topic,
            likes: (topic.likes || 0) + 1,
            liked: true
          };
        }
        return topic;
      }));
    } catch (error) {
      console.error('Error liking topic:', error);
    }
  };

  // Click "Reply" under a specific post: set reply target, pre-fill mention, and focus input
  const handleReplyToPost = (post) => {
    setReplyToPost(post);

    const mention = post?.author?.name ? `@${post.author.name} ` : '';
    setNewPostContent((current) => {
      if (!current || current === '') {
        return mention;
      }
      // If the mention is already present at the start, don't duplicate it
      if (current.startsWith(mention)) {
        return current;
      }
      return `${current}\n${mention}`;
    });

    // Focus and scroll to the reply textarea
    requestAnimationFrame(() => {
      if (replyTextareaRef.current) {
        replyTextareaRef.current.focus();
        replyTextareaRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  };

  // Precompute nested post tree (hooks must be top-level, not inside JSX)
  const nestedPostsUI = useMemo(() => {
    if (!selectedTopic) return null;

    const byParent = new Map();
    posts.forEach((p) => {
      const key = p.parentId || 'root';
      if (!byParent.has(key)) byParent.set(key, []);
      byParent.get(key).push(p);
    });

    const renderPostTree = (post, depth = 0) => {
      const children = byParent.get(post.id) || [];
      return (
        <div
          key={post.id}
          className={`p-4 ${depth > 0 ? 'ml-8 border-l border-gray-800' : ''}`}
        >
          <div className="flex">
            <div className="mr-4">
              <div className="h-10 w-10 rounded-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)]700 flex items-center justify-center">
                <span className="font-bold text-sm">{post.author.initials}</span>
              </div>
            </div>
            <div className="flex-1">
              <div className="flex justify-between items-start">
                <div>
                  <span className="font-medium">{post.author.name}</span>
                  <span className="text-xs text-gray-400 ml-2">{post.author.role}</span>
                  {!post.parentId && post.author.id === selectedTopic.author.id && (
                    <span className="ml-2 text-xs bg-blue-800 px-2 py-0.5 rounded-full">
                      Topic Creator
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-400">
                  {post.createdAt}
                </div>
              </div>
              <div className="mt-2 text-gray-300 whitespace-pre-wrap">
                {formatContentWithMentions(post.content)}
              </div>
              <div className="mt-3 flex items-center text-sm space-x-4">
                <button
                  className={`flex items-center ${post.liked ? 'text-blue-400' : 'text-gray-400 hover:text-white'}`}
                  onClick={() => !post.liked && handleLikePost(post.id)}
                  disabled={post.liked}
                >
                  <ThumbsUp size={14} className="mr-1" />
                  <span>{post.likes}</span>
                </button>
                <button
                  className="flex items-center text-gray-400 hover:text-white"
                  onClick={() => handleReplyToPost(post)}
                >
                  <MessageCircle size={14} className="mr-1" />
                  <span>Reply</span>
                </button>
                {userProfile && post.author.id === userProfile.id && (
                  <>
                    <button className="flex items-center text-gray-400 hover:text-white">
                      <Edit size={14} className="mr-1" />
                      <span>Edit</span>
                    </button>
                    <button className="flex items-center text-gray-400 hover:text-red-400">
                      <Trash size={14} className="mr-1" />
                      <span>Delete</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
          {children.length > 0 && (
            <div className="mt-3 space-y-2">
              {children.map((child) => renderPostTree(child, depth + 1))}
            </div>
          )}
        </div>
      );
    };

    const roots = byParent.get('root') || [];
    return roots.map((p) => renderPostTree(p, 0));
  }, [posts, selectedTopic, userProfile]);

  return (
    <div
      className="text-white min-h-screen bg-cover bg-center bg-no-repeat bg-fixed"
      style={{
        backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.7)), url("/assets/images/characters/Shanaya3.gif")'
      }}
    >
      <div className="container mx-auto px-3 sm:px-4 py-6 sm:py-8">
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">

            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 rounded-lg p-2 text-gray-300 hover:text-white transition-colors duration-200 border border-green-500/30 shadow-[0_0_5px_#0ff,0_0_15px_#0ff,0_0_25px_#0ff] hover:shadow-[0_0_10px_#ff0,0_0_10px_#ff0,0_0_10px_#ffb]"
            >
              ← Back to Dashboard
            </button>
            <h1 className="text-xl sm:text-2xl font-bold flex items-center">
              Community Forum
            </h1>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <div className="relative w-full sm:w-auto">
              <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search discussions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white pl-10 pr-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-64"
              />
            </div>

            <button
              className="w-full sm:w-auto flex items-center justify-center gap-2 rounded-lg p-2 text-white hover:text-white transition-colors duration-200 border border-green-500/30 shadow-[0_0_5px_#0ff,0_0_15px_#0ff,0_0_25px_#0ff] hover:shadow-[0_0_10px_#ff0,0_0_10px_#ff0,0_0_10px_#ffb]"
              onClick={() => setShowNewTopicForm(true)}
            >
              <Plus size={16} className="mr-2" />
              New Topic
            </button>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-4 sm:gap-6">
          {/* Categories sidebar */}
          <div className="w-full md:w-64 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-3 sm:p-4 backdrop-blur-sm">
            <div className="flex items-center mb-4">
              <Filter size={16} className="mr-2 text-gray-400" />
              <h2 className="font-bold">Categories</h2>
            </div>

            <div className="space-y-2">
              {categories.map(category => (
                <button
                  key={category.id}
                  className={`w-full text-left px-3 py-2 rounded-lg flex items-center transition-all duration-200 ${activeCategory === category.id
                    ? 'text-white border border-green-500/30 shadow-[0_0_5px_#0ff,0_0_15px_#0ff,0_0_25px_#0ff]'
                    : 'text-gray-400 hover:bg-[rgba(255,255,255,0.1)] hover:text-white'
                    }`}
                  onClick={() => setActiveCategory(category.id)}
                >
                  <span className="mr-2">{category.icon}</span>
                  <span>{category.name}</span>
                </button>
              ))}
            </div>

            <div className="mt-6 pt-4 border-t border-gray-700">
              <h3 className="font-bold mb-3">Forum Stats</h3>
              <div className="text-sm text-gray-400 space-y-2">
                <div className="flex justify-between">
                  <span>Total Topics:</span>
                  <span>{forumStats.totalTopics}</span>
                </div>
                <div className="flex justify-between">
                  <span>Active Users:</span>
                  <span>{forumStats.activeUsers}</span>
                </div>
                <div className="flex justify-between">
                  <span>Posts Today:</span>
                  <span>{forumStats.postsToday}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Main content area */}
          <div className="flex-1">
            {selectedTopic ? (
              /* Topic view */
              <div className="bg-[rgba(255,255,255,0.05)] border border-cyan-500/50 shadow-[0_0_10px_rgba(0,255,255,0.2)] rounded-lg overflow-hidden">
                <div className="p-4 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)]700 border-b border-cyan-500">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                    <div>
                      <h2 className="text-xl font-bold">{selectedTopic.title}</h2>
                      <div className="flex items-center text-sm mt-1">
                        <div className="flex items-center mr-4">
                          <Users size={14} className="mr-1" />
                          <span>By {selectedTopic.author.name}</span>
                        </div>
                        <div className="flex items-center mr-4">
                          <Clock size={14} className="mr-1" />
                          <span>{selectedTopic.createdAt}</span>
                        </div>
                        <div className="flex items-center">
                          <Eye size={14} className="mr-1" />
                          <span>{selectedTopic.views} views</span>
                        </div>
                      </div>
                    </div>
                      <button
                      className="text-gray-400 hover:text-white"
                      onClick={() => setSelectedTopic(null)}
                    >
                      Back to Topics
                    </button>
                  </div>
                </div>

                {/* Posts - nested by parent/child relationship */}
                <div className="divide-y divide-gray-800">
                  {nestedPostsUI}
                </div>

                {/* New post form */}
                <div className="p-4 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)]700 border-t border-gray-600">
                  <h3 className="font-bold mb-3">Post a Reply</h3>
                  <div className="mb-3 relative">
                    <textarea
                      ref={replyTextareaRef}
                      value={newPostContent}
                      onChange={handlePostContentChange}
                      onKeyDown={(e) => {
                        if (showMentionDropdown && e.key === 'ArrowDown') {
                          e.preventDefault();
                          // Could add keyboard navigation here
                        } else if (showMentionDropdown && e.key === 'Enter') {
                          e.preventDefault();
                          if (filteredParticipants.length > 0) {
                            insertMention(filteredParticipants[0]);
                          }
                        } else if (showMentionDropdown && e.key === 'Escape') {
                          setShowMentionDropdown(false);
                        }
                      }}
                      placeholder="Write your reply... (Type @ to mention someone)"
                      className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)]800 border border-gray-600 rounded-md p-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-32"
                    ></textarea>

                    {/* Mention Autocomplete Dropdown - Positioned Above (simplified) */}
                    {showMentionDropdown && filteredParticipants.length > 0 && (
                      <>
                        {/* Click-outside backdrop */}
                        <div
                          className="fixed inset-0 z-[9998] bg-black/30"
                          onClick={() => setShowMentionDropdown(false)}
                        ></div>
                        {/* Dropdown */}
                        <div
                          ref={mentionDropdownRef}
                          className="absolute z-[9999] bg-gray-900/95 border border-blue-500/70 rounded-lg shadow-2xl max-h-64 overflow-y-auto mb-2"
                          style={{ bottom: '100%', left: 0, right: 0 }}
                        >
                          {filteredParticipants.map((user) => (
                            <button
                              key={user.id}
                              type="button"
                              onClick={() => insertMention(user)}
                              className="w-full text-left px-4 py-2 hover:bg-blue-900/60 active:bg-blue-800/80 flex items-center space-x-3 transition-colors duration-150 border-b border-gray-800 last:border-b-0"
                            >
                              <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-bold">
                                {user.name.charAt(0).toUpperCase()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-white font-medium truncate">
                                  {user.name}
                                </div>
                                {user.email && (
                                  <div className="text-xs text-white-400 truncate">
                                    {user.email}
                                  </div>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="flex justify-end">
                    <button
                      className="bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-md font-medium flex items-center"
                      onClick={handleCreatePost}
                      disabled={!newPostContent.trim()}
                    >
                      <Send size={16} className="mr-2" />
                      Post Reply
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* Topics list view */
              <>
                {showNewTopicForm ? (
                  <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)]800 rounded-lg p-6">
                    <h2 className="text-xl font-bold mb-4">Create New Topic</h2>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">
                          Topic Title
                        </label>
                        <input
                          type="text"
                          value={newTopic.title}
                          onChange={(e) => setNewTopic({ ...newTopic, title: e.target.value })}
                          placeholder="Enter a descriptive title"
                          className="w-full bg-gray-800 border border-gray-600 rounded-md p-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">
                          Category
                        </label>
                        <select
                          value={newTopic.category}
                          onChange={(e) => setNewTopic({ ...newTopic, category: e.target.value })}
                          className="w-full bg-gray-800 border border-gray-600 rounded-md p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {categories.filter(c => c.id !== 'all').map(category => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">
                          Content
                        </label>
                        <textarea
                          value={newTopic.content}
                          onChange={(e) => setNewTopic({ ...newTopic, content: e.target.value })}
                          placeholder="Write your topic content here..."
                          className="w-full bg-gray-800 border border-gray-600 rounded-md p-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-32"
                        ></textarea>
                      </div>

                      <div className="flex justify-end space-x-3 pt-2">
                        <button
                          className="px-4 py-2 rounded-md border border-gray-600 hover:bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)]700"
                          onClick={() => setShowNewTopicForm(false)}
                        >
                          Cancel
                        </button>
                        <button
                          className="bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-md font-medium"
                          onClick={handleCreateTopic}
                          disabled={!newTopic.title.trim() || !newTopic.content.trim()}
                        >
                          Create Topic
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-[rgba(255,255,255,0.05)] border border-cyan-500/50 shadow-[0_0_10px_rgba(0,255,255,0.2)] rounded-lg overflow-hidden">
                    <div className="p-3 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)]700 border-b border-cyan-500 flex items-center justify-between">
                      <div className="font-bold">Topics</div>
                      <div className="text-sm text-gray-400">
                        {filteredTopics.length} {filteredTopics.length === 1 ? 'topic' : 'topics'} found
                      </div>
                    </div>

                    {loading ? (
                      <div className="flex justify-center items-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
                      </div>
                    ) : filteredTopics.length === 0 ? (
                      <div className="p-8 text-center">
                        <div className="text-5xl mb-4">🤔</div>
                        <h3 className="text-xl font-bold mb-2">No topics found</h3>
                        <p className="text-gray-400 mb-4">Be the first to start a discussion in this category!</p>
                        <button
                          className="bg-cyan-600 hover:bg-cyan-500 px-4 py-2 rounded-md font-medium inline-flex items-center"
                          onClick={() => setShowNewTopicForm(true)}
                        >
                          <Plus size={16} className="mr-2" />
                          Create New Topic
                        </button>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-700">
                        {filteredTopics.map(topic => (
                          <div
                            key={topic.id}
                            className="p-4 hover:bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)]750 border-cyan-500 cursor-pointer transition-colors"
                            onClick={() => handleTopicSelect(topic.id)}
                          >
                            <div className="flex items-start">
                              <div className={`h-10 w-10 rounded-lg ${getCategoryColor(topic.category)} flex items-center justify-center mr-4`}>
                                {getCategoryIcon(topic.category)}
                              </div>

                              <div className="flex-1">
                                <h3 className="font-bold text-base sm:text-lg">{topic.title}</h3>
                                <p className="text-gray-400 text-sm line-clamp-2 mt-1">{topic.preview}</p>

                                <div className="flex flex-wrap items-center mt-2 gap-x-3 gap-y-1 text-xs text-gray-500">
                                  <div className="flex items-center mr-4">
                                    <Users size={12} className="mr-1" />
                                    <span>{topic.author.name}</span>
                                  </div>
                                  <div className="flex items-center mr-4">
                                    <Clock size={12} className="mr-1" />
                                    <span>{topic.createdAt}</span>
                                  </div>
                                  <div className="flex items-center mr-4">
                                    <MessageCircle size={12} className="mr-1" />
                                    <span>{topic.replies} {topic.replies === 1 ? 'reply' : 'replies'}</span>
                                  </div>
                                  <button
                                    onClick={(e) => handleLikeTopic(topic.id, e)}
                                    disabled={topic.liked}
                                    className={`flex items-center ${topic.liked ? 'text-blue-400' : 'text-gray-500 hover:text-blue-400'} transition-colors`}
                                    title={topic.liked ? 'Liked' : 'Like this topic'}
                                  >
                                    <ThumbsUp size={12} className="mr-1" />
                                    <span>{topic.likes || 0}</span>
                                  </button>
                                </div>
                              </div>

                              <div className="flex flex-col items-end ml-4">
                                <div className="text-xs bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)]700 px-2 py-1 rounded-full">
                                  {getCategoryName(topic.category)}
                                </div>
                                {topic.hasNewReplies && (
                                  <div className="text-xs bg-blue-900 text-blue-300 px-2 py-1 rounded-full mt-2">
                                    New Replies
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Helper function to get category color
  function getCategoryColor(categoryId) {
    const colorMap = {
      // New arena slugs
      'phishing_social_engineering': 'bg-purple-500',
      'password_security': 'bg-blue-500',
      'data_protection': 'bg-green-500',
      'network_security': 'bg-indigo-500',
      'incident_response': 'bg-red-500',
      'best_practices': 'bg-yellow-500',
      // Legacy categories (mapped to closest arena colors)
      'general': 'bg-yellow-500',
      'social-engineering': 'bg-purple-500',
      'network-security': 'bg-indigo-500',
      'incident-response': 'bg-red-500',
      'best-practices': 'bg-yellow-500',
      'tools': 'bg-blue-500'
    };

    return colorMap[categoryId] || 'bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)]500';
  }

  // Helper function to get category icon
  function getCategoryIcon(categoryId) {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.icon : <MessageSquare size={16} />;
  }

  // Helper function to get category name
  function getCategoryName(categoryId) {
    // Map legacy category IDs to new arena names
    if (categoryId === 'general') return 'Best Practices & Tips';
    if (categoryId === 'social-engineering') return 'Phishing & Social Engineering Arena';
    if (categoryId === 'network-security') return 'Network Security Arena';
    if (categoryId === 'incident-response') return 'Incident Response Arena';
    if (categoryId === 'best-practices') return 'Best Practices & Tips';

    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : 'Uncategorized';
  }
};

export default CyberAegisForum;